'use client';

import React from 'react';

import clsx from 'clsx';
import { useTranslations } from 'next-intl';

import styles from './styles.module.css';

import { ExtendedLink } from '@/components/ExtendedLink';

export type ProductGridCardProduct = {
  id: number;
  url: string;
  imageSrc: string;
  alt: string;
  /** Optional override for the product link aria-label (e.g. "View product X by @user"). */
  ariaLabel?: string;
  /** Optional srcSet for responsive images (e.g. "url 84w, url 101w"). */
  srcSet?: string;
  /** Optional sizes attribute when using srcSet (e.g. "(max-width: 767px) 84px, 101px"). */
  sizes?: string;
  width?: number;
  height?: number;
};

type Props = {
  products: ProductGridCardProduct[];
  productsListAriaLabel: string;
  onProductLinkClick?: (product: ProductGridCardProduct) => void;
  children: React.ReactNode;
};

export function ProductGridCard({
  products,
  productsListAriaLabel,
  onProductLinkClick,
  children,
}: Props) {
  const t = useTranslations('common');
  const ulProps = {
    className: clsx(styles.products, styles.productsTablet3),
    'aria-label': productsListAriaLabel,
  };

  function getProductExtendedLinkProps(product: ProductGridCardProduct) {
    return {
      className: styles.productLink,
      href: product.url,
      as: product.url,
      'aria-label':
        product.ariaLabel ??
        t('ProductGridCard.ViewProduct', { alt: product.alt }),
      onClick: () => onProductLinkClick?.(product),
    };
  }

  function getProductImageProps(product: ProductGridCardProduct) {
    return {
      src: product.imageSrc,
      srcSet: product.srcSet,
      sizes: product.sizes,
      width: product.width,
      height: product.height,
      className: styles.productImage,
      loading: 'lazy' as const,
      ...(product.alt === '' && { role: 'presentation' as const }),
    };
  }

  return (
    <article className={styles.card}>
      <ul {...ulProps}>
        {products.map((product) => (
          <li key={product.id}>
            <ExtendedLink {...getProductExtendedLinkProps(product)}>
              <img {...getProductImageProps(product)} alt={product.alt} />
            </ExtendedLink>
          </li>
        ))}
      </ul>
      {children}
    </article>
  );
}
